import { useState, useTransition } from "react";
import { Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { consumeReturnUrl } from "../../utils/authRedirect";
import { Button, Grid, TextField, Typography } from "@mui/material";

import {
  parseErrorMessage,
  sendOTP,
  verifyOTP,
  type VerifyOTPResult,
} from "../../services/webapi";
import { useUser } from "../../hooks/useUser";
import { processLoginResponse } from "../../services/userService";
import HomePageLayout from "../../components/layout/HomePageLayout";
import CountryCodeDropdown from "../../components/ui/CountryCodeDropdown";

interface LoginProps {
  locale: string;
  loadAccount: () => Promise<void>;
  location?: {
    state?: {
      timeout?: boolean;
    };
  };
}

export default function LoginPage({
  locale,
  loadAccount,
  location,
}: LoginProps) {
  const { login, isAuthenticated } = useUser();
  const [formData, setFormData] = useState({
    phone: "",
    otp: "",
  });
  const [countryCode, setCountryCode] = useState("");
  const [error, setError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [secret, setSecret] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();

  const isSessionTimeout =
    location?.state?.timeout === true ||
    searchParams.get("timeout") === "true" ||
    new URLSearchParams(routerLocation.search).get("timeout") === "true";

  if (isAuthenticated) {
    const returnUrl = consumeReturnUrl(searchParams.get("returnUrl"));
    return <Navigate to={returnUrl || "/dashboard"} replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryCodeChange = (code: string) => {
    setCountryCode(code);
  };

  const handleGetOTP = async (e: React.MouseEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.phone) {
      setError("Please enter your phone number");
      return;
    }

    if (!countryCode) {
      setError("Please select a country code");
      return;
    }

    startTransition(async () => {
      try {
        const secret = await sendOTP(countryCode + formData.phone);
        setOtpSent(true);
        setSecret(secret);
      } catch (err) {
        setError(parseErrorMessage(err, locale) || "Failed to send OTP");
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.otp) {
      setError("Please enter the OTP");
      return;
    }

    startTransition(async () => {
      try {
        const result: VerifyOTPResult = await verifyOTP(secret, formData.otp);
        const { token, userName, id, firstName, lastName, timezone } = result;

        const userData = processLoginResponse({
          token,
          user: {
            id,
            name: userName,
            phone: countryCode + formData.phone,
            firstName,
            lastName,
            timezone,
          },
        });
        login(userData);
        await loadAccount();
        const returnUrl = consumeReturnUrl(searchParams.get("returnUrl"));
        navigate(returnUrl || "/dashboard", { replace: true });
      } catch (err) {
        setError(parseErrorMessage(err, locale) || "Invalid OTP");
      }
    });
  };

  return (
    <HomePageLayout>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography
            variant="h4"
            color="info"
            align="center"
            fontWeight={"bold"}
          >
            Welcome to ReliablyME User Console
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1" color="success" align="center">
            Please log in to continue
          </Typography>
        </Grid>
        <Grid size={12}>
          <form onSubmit={handleSubmit} className="auth__form">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 12, lg: 5 }}>
                <CountryCodeDropdown onChange={handleCountryCodeChange} />
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 7 }}>
                <TextField
                  name="phone"
                  variant="outlined"
                  value={formData.phone}
                  onChange={handleChange}
                  label="Phone Number"
                  fullWidth={true}
                  disabled={otpSent}
                />
              </Grid>

              {otpSent && (
                <Grid size={12}>
                  <TextField
                    name="otp"
                    variant="outlined"
                    value={formData.otp}
                    onChange={handleChange}
                    label="Enter OTP"
                    fullWidth={true}
                    autoFocus
                  />
                </Grid>
              )}

              <Grid size={12}>
                {error && (
                  <Typography variant="subtitle1" color="error">
                    {parseErrorMessage(error, locale)}
                  </Typography>
                )}

                {isSessionTimeout && (
                  <Typography
                    variant="subtitle1"
                    color="warning"
                    data-testid="session-timeout-notice"
                  >
                    Session timed out
                  </Typography>
                )}
              </Grid>

              <Grid size={12}>
                {!otpSent ? (
                  <Button
                    onClick={handleGetOTP}
                    variant="contained"
                    color="primary"
                    fullWidth={true}
                    disabled={!formData.phone || !countryCode || isPending}
                  >
                    Get OTP
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth={true}
                    disabled={!formData.otp || isPending}
                  >
                    Login
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </HomePageLayout>
  );
}
